import React from 'react';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, FlexRow, Icon, Text} from '@forecast-it/design-system';
import {
	FEATURE_FLAG_CARD_BORDER,
	FEATURE_FLAG_NEUTRAL_FILL,
	FeatureFlagSelectableRow,
	FeatureFlagSelectableRowLabel,
} from '../featureFlagSurface';
import {BETA_PROGRAM_COHORT_ID, RolloutCohortOption} from './featureFlagRolloutCohort';

/** How many skipped companies the notice names before trailing off. */
const SKIP_EXAMPLE_LIMIT = 4;

const MicroLabel = styled.span`
	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

const CohortList = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 6px;
	border: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 4px;
	overflow: hidden;
`;

/* Stacks the cohort's name over its skip hint, so the count stays aligned to the row's right edge
   whether or not a hint is present. */
const CohortIdentity = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	text-align: left;
`;

const Footnote = styled.div`
	margin-top: 8px;
`;

const Strip = styled.div`
	display: flex;
	gap: 10px;
	margin-top: 14px;
	padding: 12px 14px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-radius: 4px;
`;

const StripIcon = styled.span`
	flex: none;
	margin-top: 1px;
`;

/** Matches how the manage-lists sidebar counts the same cohort. */
function companyCountLabel(count: number): string {
	return count === 1 ? '1 company' : `${count} companies`;
}

/**
 * What a cohort's row says about the members a rollout would skip.
 *
 * Shown on the row rather than only after picking, because it is what decides whether a cohort is
 * worth picking at all — a cohort whose every member already holds the flag has nothing to add.
 */
function skipHint(option: RolloutCohortOption): string {
	const verb = option.alreadyGrantedCount === 1 ? 'has' : 'have';
	return `${option.alreadyGrantedCount} of ${option.memberCount} already ${verb} the flag`;
}

/**
 * Whether anything on offer is a cohort somebody made, rather than the standing beta program.
 *
 * The beta program is always offered, so the picker is never empty and there is no empty state to
 * explain itself. A tenant that has created no cohorts of its own still needs telling where cohorts
 * come from, and the beta row alone does not say it.
 */
function hasEditableCohorts(options: readonly RolloutCohortOption[]): boolean {
	return options.some(option => option.cohortId !== BETA_PROGRAM_COHORT_ID);
}

/** The first few names, trailing off when there are more. */
function examples(values: readonly string[]): string {
	const shown = values.slice(0, SKIP_EXAMPLE_LIMIT).join(', ');
	return values.length > SKIP_EXAMPLE_LIMIT ? `${shown}…` : shown;
}

function alreadyGrantedMessage(names: readonly string[]): string {
	return names.length === 1
		? `1 company in this cohort already has the flag: ${examples(names)}`
		: `${names.length} companies in this cohort already have the flag: ${examples(names)}`;
}

export interface RolloutCohortTabProps {
	/** The cohorts to offer, in the order they are listed. */
	options: readonly RolloutCohortOption[];
	/** The picked cohort, or null before anything is picked. */
	selectedCohortId: string | null;
	onSelectCohort: (cohortId: string) => void;
	/** Names of the picked cohort's members that already hold the flag, and so will be skipped. */
	alreadyGrantedNames: readonly string[];
}

/**
 * Picks one cohort to roll out to. Single-select: a cohort is a curated list, so rolling out to two at
 * once is a combination better expressed by editing the lists themselves.
 *
 * Holds no state — the modal owns the selection, because changing it has to clear which companies the
 * shared preview is holding back.
 */
export function RolloutCohortTab({
	options,
	selectedCohortId,
	onSelectCohort,
	alreadyGrantedNames,
}: RolloutCohortTabProps): JSX.Element {
	return (
		<div>
			<MicroLabel>
				<Detail size="2">SELECT A COHORT</Detail>
			</MicroLabel>
			<CohortList>
				{options.map(option => {
					const selected = option.cohortId === selectedCohortId;
					return (
						<FeatureFlagSelectableRow key={option.cohortId} $selected={selected}>
							<Button
								data-cy={`rollout-cohort-${option.cohortId}`}
								emphasis={EMPHASIS.LOW}
								fullWidth
								onPress={() => onSelectCohort(option.cohortId)}
							>
								<FlexRow gap="xs" justifyContent="space-between" alignItems="center">
									<CohortIdentity>
										<FeatureFlagSelectableRowLabel $selected={selected}>
											<Text>{option.name}</Text>
										</FeatureFlagSelectableRowLabel>
										{option.alreadyGrantedCount === 0 ? null : (
											<Detail color="medium">{skipHint(option)}</Detail>
										)}
									</CohortIdentity>
									<Detail color="medium">{companyCountLabel(option.memberCount)}</Detail>
								</FlexRow>
							</Button>
						</FeatureFlagSelectableRow>
					);
				})}
			</CohortList>
			<Footnote>
				<Detail color="medium">Every company in the cohort is added regardless of the current percentage.</Detail>
			</Footnote>
			{hasEditableCohorts(options) ? null : (
				<Footnote>
					<Detail color="medium">
						Cohorts are shared across every feature flag and are created on the manage lists screen, reached from
						the feature flag list.
					</Detail>
				</Footnote>
			)}
			{alreadyGrantedNames.length === 0 ? null : (
				<Strip>
					<StripIcon>
						<Icon icon="info" size="s" />
					</StripIcon>
					<Detail color="medium">{alreadyGrantedMessage(alreadyGrantedNames)}</Detail>
				</Strip>
			)}
		</div>
	);
}
